import { useState, useEffect, useMemo, useCallback } from 'react';
import { Form, Input, Select, InputNumber, Button, Card, message } from 'antd';
import { useCommunityAuth } from '../../../contexts/CommunityAuthContext';
import RichTextEditor from '../RichTextEditor';
import communityAPI from '../../../api/community';
import { useMetadata } from '../../../hooks/useCommunity';
import { getCreationRestrictionCopy } from '../../../utils/restrictionCopy';
import { extractApiError } from '../../../utils/apiError';

const useDarkMode = () => {
  const [isDarkMode, setIsDarkMode] = useState(() =>
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
  );
  useEffect(() => {
    const checkTheme = () => setIsDarkMode(document.documentElement.classList.contains('dark'));
    const observer = new MutationObserver(checkTheme);
    if (typeof document !== 'undefined') {
      observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    }
    return () => observer.disconnect();
  }, []);
  return isDarkMode;
};

// Reusable Challenge creation component styled similar to LearningPathCreate / CreatePost
export default function ChallengeCreate({ onCreated }) {
  const { user, isAdmin } = useCommunityAuth();
  const isDarkMode = useDarkMode();
  const [form] = Form.useForm();
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const { categories, tags, loading: metadataLoading } = useMetadata();
  const creationRestriction = useMemo(
    () => getCreationRestrictionCopy(user?.status, 'Challenge'),
    [user?.status]
  );
  const creationBlocked = Boolean(creationRestriction.blocked);
  const categoryOptions = useMemo(
    () =>
      categories.map((category) => ({
        value: String(category.id),
        label: category.name,
        slug: category.slug || '',
      })),
    [categories]
  );
  const tagOptions = useMemo(
    () =>
      tags.map((tag) => ({
        value: String(tag.id),
        label: tag.name,
        slug: tag.slug || '',
      })),
    [tags]
  );
  const filterByLabelOrSlug = useCallback((inputValue, option) => {
    if (!option) {
      return false;
    }
    const text = `${option.label ?? ''} ${option.slug ?? ''} ${option.value ?? ''}`;
    return text.toLowerCase().includes((inputValue || '').toLowerCase());
  }, []);

  useEffect(() => {
    form.setFieldsValue({
      title: '',
      category: undefined,
      tags: [],
      difficulty: 'easy',
      points: 100,
      flag: '',
    });
  }, [form]);

  const handleCreate = async (values) => {
    if (!user) {
      message.error('Login required');
      return;
    }
    if (!isAdmin) {
      message.error('Only administrators can create challenges.');
      return;
    }
    const text = (description || '').replace(/<[^>]*>/g, '').trim();
    if (!text) {
      message.error('Description is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...values,
        description,
        category: values.category || null,
        tags: (values.tags || []).filter((tag) => tag !== null && tag !== ''),
      };
      const res = await communityAPI.challenges.create(payload);
      message.success('Challenge created');
      const pendingTags = Array.isArray(res?.data?.pending_tags) ? res.data.pending_tags : [];
      if (pendingTags.length) {
        const label = pendingTags.map((tag) => `"${tag.name}"`).join(', ');
        message.info(
          `Tag${pendingTags.length > 1 ? 's' : ''} ${label} submitted for moderator review.`
        );
      }
      form.resetFields();
      setDescription('');
      onCreated && onCreated(res.data);
    } catch (err) {
      console.error('Failed to create challenge:', err);
      const errorMessage = extractApiError(err, 'Failed to create challenge');
      message.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <Card className="p-6 rounded-2xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
        <h3 className="text-lg font-semibold text-red-700 dark:text-red-300 mb-2">Authentication Required</h3>
        <p className="text-sm text-red-600 dark:text-red-400">Please log in to create challenges.</p>
      </Card>
    );
  }

  if (!isAdmin) {
    return (
      <Card className="p-6 rounded-2xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
        <h3 className="text-lg font-semibold text-blue-700 dark:text-blue-300 mb-2">Administrator Access Needed</h3>
        <p className="text-sm text-blue-600 dark:text-blue-200/80">Only community administrators can create new challenges.</p>
        <p className="mt-2 text-xs text-blue-500/80 dark:text-blue-200/60">Reach out to an admin if you need a challenge published.</p>
      </Card>
    );
  }

  if (creationBlocked) {
    return (
      <Card className="p-6 rounded-2xl border border-amber-300/60 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-900/20">
        <h3 className="text-lg font-semibold text-amber-700 dark:text-amber-200 mb-2">{creationRestriction.headline}</h3>
        <p className="text-sm text-amber-700/80 dark:text-amber-100">{creationRestriction.detail}</p>
        <p className="mt-2 text-xs text-amber-600/80 dark:text-amber-200/70">
          {creationRestriction.support || 'Contact a community administrator if you believe this is an error.'}
        </p>
      </Card>
    );
  }

  return (
    <Card className="rounded-2xl shadow-xl border border-gray-100 dark:border-gray-800 overflow-hidden dark:bg-[#161616]">
      <div className="p-6 bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10 dark:from-indigo-900/20 dark:via-purple-900/20 dark:to-pink-900/20 border-b border-gray-100 dark:border-gray-800">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Create New Challenge</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">Set up a CTF task for the community.</p>
      </div>
      <div className="p-6">
        <Form form={form} layout="vertical" onFinish={handleCreate} className="space-y-6 [&_.ant-form-item-label>label]:dark:!text-gray-200 [&_.ant-input]:dark:!bg-[#0E0E0E] [&_.ant-input]:dark:!border-gray-800 [&_.ant-input]:dark:!text-white [&_.ant-input]:dark:!placeholder-gray-500 [&_.ant-select-selector]:dark:!bg-[#0E0E0E] [&_.ant-select-selector]:dark:!border-gray-800 [&_.ant-select-selection-item]:dark:!text-white [&_.ant-select-selection-placeholder]:dark:!text-gray-500 [&_.ant-input-number]:dark:!bg-[#0E0E0E] [&_.ant-input-number]:dark:!border-gray-800 [&_.ant-input-number-input]:dark:!text-white [&_.ant-form-item-explain]:dark:!text-gray-400 [&_.ant-form-item-extra]:dark:!text-gray-500">
          <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Enter a title' }]}> 
            <Input placeholder="Challenge title" />
          </Form.Item>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Form.Item name="category" label="Category" rules={[{ required: true, message: 'Select a category' }]}> 
              <Select
                placeholder="Select category"
                allowClear
                loading={metadataLoading}
                showSearch
                filterOption={filterByLabelOrSlug}
                options={categoryOptions}
              />
            </Form.Item>
            
          <Form.Item
            name="tags"
            label="Tags"
            extra="Choose from existing tags when you can. New tags will stay hidden until a moderator approves them."
          >
            <Select
              mode="tags"
              allowClear
              placeholder="Select or create tags"
              loading={metadataLoading}
              showSearch
              popupMatchSelectWidth={false}
              filterOption={filterByLabelOrSlug}
              options={tagOptions}
            />
          </Form.Item>
            <Form.Item name="difficulty" label="Difficulty" rules={[{ required: true }]}> 
              <Select>
                <Select.Option value="easy">Easy</Select.Option>
                <Select.Option value="medium">Medium</Select.Option>
                <Select.Option value="hard">Hard</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item name="points" label="Points" rules={[{ required: true }]}> 
              <InputNumber min={10} step={10} className="w-full" />
            </Form.Item>
          </div>


          <Form.Item label="Description" required>
            <RichTextEditor
              form={form}
              isDarkMode={isDarkMode}
              fieldName="description"
              value={description}
              onChange={setDescription}
              placeholder="Describe the challenge, include any hints or constraints..."
              onSave={() => form.submit()}
            />
          </Form.Item>

            <Form.Item name="flag" label="Flag" rules={[{ required: true, message: 'Provide a flag pattern' }]}> 
              <Input placeholder="flag{example_flag}" />
            </Form.Item>

          <div className="flex gap-4">
            <Button type="primary" htmlType="submit" loading={saving} disabled={!description.trim() || metadataLoading} className="px-6 py-2 rounded-xl bg-gradient-to-r from-primary to-secondary hover:from-indigo-700 hover:to-purple-700">
              Create Challenge
            </Button>
            <Button htmlType="button" onClick={() => { form.resetFields(); setDescription(''); }}>
              Reset
            </Button>
          </div>
        </Form>
      </div>
    </Card>
  );
}